import { Component, OnInit, Input, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { PrimaryFunctions, FunctionsPerformed } from '@xbs/xbs-enums';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PbasService, PlanningDataService, PartyRolesService, PlanningApiService, PlanningFormService } from '../../../shared/services';

@Component({
  selector: 'transaction-and-tested-party',
  templateUrl: './transaction-and-tested-party.component.html',
  styleUrls: ['./transaction-and-tested-party.component.css']
})
export class TransactionAndTestedPartyComponent implements OnInit, OnDestroy, OnChanges {

  @Input() projection;

  constructor(
    private _partyRolesService: PartyRolesService,
    private _pbasService: PbasService,
    private _planningApiService: PlanningApiService,
    private _planningDataService: PlanningDataService,
    private _planningFormService: PlanningFormService,
    private _translateService: TranslateService
  ) { }
  FormTitle = 'transactionAndTestedPartyForm';
  selectedTransactionType;
  testedPartyPair;
  selectedTestedParty;
  currentProjection;
  transactionTestedPartyForm: FormGroup;
  industryValueOptions;
  currentSelectedCharacterization;

  allMajorServiceClassifications;
  partyRoles;

  currentSelectedClassifications = [];

  ngOnInit() {
    this.setupSicCodesForManufacturers();
    this.getAllPartyRoles();
    this.getAllMajorServiceClassifications();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.projection.firstChange || this.currentProjection.projectionId !== changes.projection.currentValue.projectionId) {
      this.currentProjection = changes.projection.currentValue;
      this.createTestedPartyPair();
      this.transactionTestedPartyForm = new FormGroup({
        transactionValue: new FormControl(this.currentProjection.transactionValue || null,
          [Validators.required, Validators.min(0)]
        ),
        selectedTransactionType: new FormControl(null,
          [Validators.required]
        )
      });
      if (this.currentProjection.testedPartyCharacterization) {
        this.setCharacterization(this.currentProjection.testedPartyCharacterization.Id);
      }
      if (this.currentProjection.classifications) {
        this.updateMajorsSelected(this.currentProjection.classifications.selectedClassifications);
      }

      if (this.currentProjection.transactionType && this.partyRoles) {
        let selectedIndex;
        this.partyRoles.forEach((role, index) => {
          if (role.partyRoleId === this.currentProjection.transactionType.partyRoleId) {
            selectedIndex = index;
          }
        });
        this.transactionTestedPartyForm.patchValue({ 'selectedTransactionType': this.partyRoles[selectedIndex] });
      }

      this.formControlValueChanged();
    }
  }

  formControlValueChanged() {
    this.transactionTestedPartyForm.statusChanges.subscribe(
      (form) => {
        if (form === 'VALID' && this.selectedTestedParty) {
          this._planningFormService.setFormStatus(this.FormTitle, true);
        } else {
          this._planningFormService.setFormStatus(this.FormTitle, false);
        }

        const currentFormStatus = this._planningFormService.getCurrentFormStatus();
        this._planningDataService.updateFormValues(currentFormStatus, this.currentProjection.projectionId);
      });
  }

  saveForm() {
    this._planningApiService.saveScenario(this._planningDataService.getCurrentScenarioValue()).subscribe();
  }

  ngOnDestroy() {
    this.saveForm();
  }

  createTestedPartyPair() {
    this.testedPartyPair = [
      this.currentProjection.srcEntity,
      this.currentProjection.dstEntity
    ];

    if (this.currentProjection.srcEntity.isTestedParty) {
      this.selectedTestedParty = this.currentProjection.srcEntity;
    } else if (this.projection.dstEntity.isTestedParty) {
      this.selectedTestedParty = this.currentProjection.dstEntity;
    } else {
      this.selectedTestedParty = null;
    }
  }

  setCharacterization(characterizationId: number) {
    this.currentSelectedCharacterization = PrimaryFunctions.ById[characterizationId];
    this.currentProjection.testedPartyCharacterization = PrimaryFunctions.ById[characterizationId];
    this.currentProjection.testedPartyCharacterization.primaryFunctionId = this.currentProjection.testedPartyCharacterization.Id;

    if (characterizationId === 2) {
      this.transactionTestedPartyForm.patchValue({
        'majorServiceClassifications': null
      }, { emitEvent: false });
      this.currentProjection.classifications = {
        selectedClassifications: null
      };
      this.transactionTestedPartyForm.removeControl('majorServiceClassifications');
      this.transactionTestedPartyForm.addControl('valueAddedDistributor', new FormControl(this.currentProjection.characterization.valueAddedDistributor || 0));
      this.transactionTestedPartyForm.addControl('sicCode',
        new FormControl(
          this.currentProjection.characterization.sicCode || null,
          [Validators.required]
        )
      );

      this.setValueAdded();
    } else if (characterizationId === 3) {
      this.currentProjection.characterization = {
        valueAddedDistributor: null,
        sicCode: null
      };
      this.transactionTestedPartyForm.removeControl('valueAddedDistributor');
      this.transactionTestedPartyForm.removeControl('sicCode');
      this.transactionTestedPartyForm.addControl('majorServiceClassifications',
        new FormControl(
          this.currentProjection.classifications && this.currentProjection.classifications.selectedClassifications || null,
          [Validators.required, Validators.minLength(1)]
        )
      );

      this.updateMajorsSelected(this.currentProjection.classifications.selectedClassifications);
    }
    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
    this.saveForm();
  }

  setSicCodes(industrialCode) {
    this.currentProjection.characterization.sicCode = industrialCode;
    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
  }

  setTestedParty(entity) {
    if (this.currentProjection.srcEntity.testedPartyId === entity.testedPartyId) {
      this.currentProjection.srcEntity.isTestedParty = true;
      this.currentProjection.dstEntity.isTestedParty = false;
    } else if (this.currentProjection.dstEntity.testedPartyId === entity.testedPartyId) {
      this.currentProjection.dstEntity.isTestedParty = true;
      this.currentProjection.srcEntity.isTestedParty = false;
    }
    this.selectedTestedParty = entity;
    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
    this.saveForm();
  }

  changeSetTestedParty() {
    const entityToSwitchTo = this.testedPartyPair.filter((entity) => entity !== this.selectedTestedParty)[0];
    this.setTestedParty(entityToSwitchTo);
  }

  setTransactionValue($event: string) {
    if (typeof ($event) === 'string') {
      $event = $event.replace(/,/g, '');
      const parsedEvent = parseInt($event, 10);
      this.transactionTestedPartyForm.patchValue({ 'transactionValue': parsedEvent }, { emitEvent: false });
      this.currentProjection.transactionValue = parsedEvent;
      this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
    }
  }

  setTransactionType($event) {
    if (this.currentProjection.transactionType !== $event) {
      this.currentProjection.transactionType = $event;
      this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
    }
  }

  setValueAdded() {
    const valueAddedState = this.transactionTestedPartyForm.value.valueAddedDistributor;
    if (valueAddedState) {
      this.currentProjection.characterization.valueAddedDistributor = FunctionsPerformed.ByName.HasValueAdded.Id;
    } else {
      this.currentProjection.characterization.valueAddedDistributor = FunctionsPerformed.ByName.DoesNotHaveValueAdded.Id;
    }

    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
  }

  updateMajorsSelected($event) {
    this.currentSelectedClassifications = [];
    $event = $event || [];

    $event.forEach((mjr) => {
      // Add predicate here to make code more readable
      if (!this.transactionTestedPartyForm.value[mjr.majorServiceClassificationId]) {
        this.transactionTestedPartyForm.registerControl(`${mjr.majorServiceClassificationId}`, new FormControl(
          mjr.selectedMinors || null,
          [Validators.required, Validators.minLength(1)]
        ));
      }
      this.currentSelectedClassifications.push(mjr);
    });

    this.transactionTestedPartyForm.updateValueAndValidity();

    this.currentProjection.classifications = {
      selectedClassifications: this.currentSelectedClassifications
    };

    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
  }

  updateSelectedMinors(selectedMinors, majorServiceClassificationId) {
    const selectedMajor = this.currentProjection.classifications.selectedClassifications.filter((major) => {
      return major.majorServiceClassificationId === majorServiceClassificationId;
    })[0];

    selectedMajor.selectedMinors = [];

    selectedMinors.forEach((minor) => {
      selectedMajor.selectedMinors.push(minor);
    });

    this.currentProjection.classifications.selectedClassifications.map((major) => {
      if (major.majorServiceClassificationId === selectedMajor.majorServiceClassificationId) {
        major = selectedMajor;
      }
      return major;
    });

    this._planningDataService.updateProjection(this.currentProjection.projectionId, this.currentProjection);
  }

  private getAllPartyRoles(): void {
    this._partyRolesService.list().subscribe(({ data }) => {
      this.partyRoles = data;
      if (this.currentProjection.transactionType) {
        let selectedIndex;
        this.partyRoles.forEach((role, index) => {
          if (role.partyRoleId === this.currentProjection.transactionType.partyRoleId) {
            selectedIndex = index;
          }
        });
        this.transactionTestedPartyForm.patchValue({ 'selectedTransactionType': this.partyRoles[selectedIndex] });
      }
    });
  }

  private setupSicCodesForManufacturers() {
    this._pbasService
      .getTypeOfOptions(2)
      .subscribe(({ data }) => {
        // typeOfPrimaryFunctionOptionId === 2 is for manufacturers only
        const options = data.find((item) => item.typeOfPrimaryFunctionOptionId === 2);
        this.industryValueOptions = options.codeTypes.find((item) => item.name === 'SIC').subtypes;
        this.industryValueOptions = this.industryValueOptions.map((option) => {
          option.displayLabel = `${option.value} - ${this._translateService.instant(option.name)}`;
          return option;
        });
      });
  }

  private getAllMajorServiceClassifications(): void {
    this._pbasService.listAllMajorServiceClassifications().subscribe(({ data }) => {
      this.allMajorServiceClassifications = data;
      this.allMajorServiceClassifications = this.allMajorServiceClassifications.map((mjr) => {
        mjr.displayLabel = `${this._translateService.instant(mjr.name)}`;
        mjr.minorServiceClassifications.forEach((minor) => {
          minor.majorServiceClassificationId = mjr.majorServiceClassificationId;
          minor.displayLabel = `${this._translateService.instant(minor.name)}`;
          return minor;
        });
        return mjr;
      });

    });
  }

}
